import { useState, useCallback, useRef, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useInvoices, useExport, downloadInvoicePdf, openInvoicePdfForPrint, downloadCreditNotePdf, openCreditNotePdfForPrint, useSendInvoiceEmail } from '@/hooks/useApi';
import Tooltip from '@/components/Tooltip';
import PageSkeleton from '@/components/PageSkeleton';
import ActivityModal from '@/components/ActivityModal';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

/* ── SVG icon components ── */
const DownloadIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const PrintIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
  </svg>
);


const EnvelopeIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);
const ViewIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const INVOICE_STATUSES = ['draft', 'sent', 'viewed', 'partially_paid', 'paid', 'overdue', 'cancelled', 'credited'] as const;

const STATUS_COLORS: Record<string, string> = {
  draft: 'badge-gray',
  sent: 'badge-blue',
  viewed: 'badge-blue',
  partially_paid: 'badge-yellow',
  paid: 'badge-green',
  overdue: 'badge-red',
  cancelled: 'badge-gray',
  credited: 'badge-gray',
};

const ROW_COLORS: Record<string, string> = {
  draft: 'row-gray',
  sent: 'row-blue',
  viewed: 'row-sky',
  partially_paid: 'row-amber',
  paid: 'row-green',
  overdue: 'row-red',
  cancelled: 'row-gray',
  credited: 'row-slate',
};

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'es', label: 'Español' },
];

const SendIcon = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

function getInvoiceTarget(invoiceId: string, status: string): string {
  return status === 'draft' ? `/invoices/${invoiceId}/edit` : `/invoices/${invoiceId}`;
}

export default function InvoicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters from URL
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const statusFilter = searchParams.get('status') || '';
  const page = Number(searchParams.get('page')) || 1;

  // Advanced search state
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [clientFilter, setClientFilter] = useState(searchParams.get('client') || '');
  const [dateFrom, setDateFrom] = useState(searchParams.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('dateTo') || '');
  const [amountMin, setAmountMin] = useState(searchParams.get('amountMin') || '');
  const [amountMax, setAmountMax] = useState(searchParams.get('amountMax') || '');

  // Export date range
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const exportRef = useRef<HTMLDivElement>(null);

  // Debounce ref
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Use the debounced search from URL params for the API call (not the instant input state)
  const debouncedSearch = searchParams.get('search') || '';

  // Build query params for the API call
  const queryParams: Record<string, unknown> = { page };
  if (debouncedSearch) queryParams.search = debouncedSearch;
  if (statusFilter) queryParams.status = statusFilter;
  if (clientFilter) queryParams.client = clientFilter;
  if (dateFrom) queryParams.dateFrom = dateFrom;
  if (dateTo) queryParams.dateTo = dateTo;
  if (amountMin) queryParams.amountMin = amountMin;
  if (amountMax) queryParams.amountMax = amountMax;

  const { data, isLoading, isFetching } = useInvoices(queryParams);
  const exportMutation = useExport();
  const sendInvoiceEmail = useSendInvoiceEmail();
  const [sendModalInvoice, setSendModalInvoice] = useState<any>(null);
  const [sendLanguage, setSendLanguage] = useState('en');
  const [activityInvoice, setActivityInvoice] = useState<{ id: string; number: string } | null>(null);

  // Close export panel on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function setFilter(key: string, value: string) {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value);
    else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  }

  function setMultipleFilters(filters: Record<string, string>) {
    const p = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(filters)) {
      if (value) p.set(key, value);
      else p.delete(key);
    }
    p.delete('page');
    setSearchParams(p);
  }

  // Debounced search
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilter('search', value);
    }, 300);
  }, [searchParams]);

  // Reset all filters
  const handleReset = () => {
    setSearch('');
    setClientFilter('');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
    setSearchParams({});
  };

  // Apply advanced filters
  const applyAdvancedFilters = () => {
    setMultipleFilters({
      search,
      status: statusFilter,
      client: clientFilter,
      dateFrom,
      dateTo,
      amountMin,
      amountMax,
    });
  };

  // Export with date range
  const handleExport = () => {
    const params: Record<string, string> = {};
    if (exportFrom) params.dateFrom = exportFrom;
    if (exportTo) params.dateTo = exportTo;
    exportMutation.mutate({ type: 'invoices', params });
    setExportOpen(false);
  };

  const hasActiveFilters = search || statusFilter || clientFilter || dateFrom || dateTo || amountMin || amountMax;

  if (isLoading && !data) return <PageSkeleton />;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold whitespace-nowrap">{t('invoices.title')}</h1>
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder={`${t('common.search')}... (${t('invoices.number')}, ${t('invoices.client')}, ${t('common.amount')}...)`}
            className="input pl-10 w-full"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        {/* Export dropdown */}
        <div className="relative" ref={exportRef}>
          <button className="btn btn-secondary" onClick={() => setExportOpen(!exportOpen)}>
            {t('common.export')}
          </button>
          {exportOpen && (
            <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50">
              <h4 className="text-sm font-medium text-gray-900 mb-3">{t('invoices.exportByDate')}</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{t('invoices.dateFrom')}</label>
                  <input
                    type="date"
                    className="input text-sm"
                    value={exportFrom}
                    onChange={(e) => setExportFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{t('invoices.dateTo')}</label>
                  <input
                    type="date"
                    className="input text-sm"
                    value={exportTo}
                    onChange={(e) => setExportTo(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <button className="btn btn-primary btn-sm flex-1" onClick={handleExport}>
                    {exportMutation.isPending ? t('common.loading') : t('common.export')}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setExportFrom(''); setExportTo(''); }}>
                    {t('invoices.resetFilters')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <Link to="/invoices/new" className="btn btn-primary whitespace-nowrap">
          + {t('invoices.createInvoice')}
        </Link>
      </div>

      {/* Filters */}
      <div className="card card-body mb-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            className="input w-auto"
            value={statusFilter}
            onChange={(e) => setFilter('status', e.target.value)}
          >
            <option value="">{t('common.allStatuses')}</option>
            {INVOICE_STATUSES.map((s) => (
              <option key={s} value={s}>{t(`invoices.status.${s}`)}</option>
            ))}
          </select>
          <button
            onClick={() => setAdvancedOpen(!advancedOpen)}
            className={`btn btn-sm ${advancedOpen ? 'btn-primary' : 'btn-secondary'}`}
          >
            <svg className="h-4 w-4 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {t('invoices.advancedSearch')}
          </button>
          {hasActiveFilters && (
            <button onClick={handleReset} className="btn btn-sm btn-secondary text-red-600 hover:text-red-700 hover:bg-red-50">
              <svg className="h-4 w-4 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {t('invoices.resetFilters')}
            </button>
          )}
        </div>

        {/* Advanced search panel */}
        {advancedOpen && (
          <div className="border-t border-gray-100 pt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('invoices.client')}</label>
              <input
                type="text"
                className="input text-sm"
                placeholder={t('invoices.client')}
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('invoices.dateFrom')}</label>
              <input
                type="date"
                className="input text-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">{t('invoices.dateTo')}</label>
              <input
                type="date"
                className="input text-sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-xs text-gray-500 block mb-1">{t('invoices.amountMin')}</label>
                <input
                  type="number"
                  className="input text-sm"
                  placeholder="0.00"
                  value={amountMin}
                  onChange={(e) => setAmountMin(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-gray-500 block mb-1">{t('invoices.amountMax')}</label>
                <input
                  type="number"
                  className="input text-sm"
                  placeholder="999999"
                  value={amountMax}
                  onChange={(e) => setAmountMax(e.target.value)}
                />
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-4 flex justify-end gap-2 pt-1">
              <button onClick={handleReset} className="btn btn-sm btn-secondary">
                {t('invoices.resetFilters')}
              </button>
              <button onClick={applyAdvancedFilters} className="btn btn-sm btn-primary">
                {t('invoices.applyFilters')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('invoices.number')}</th>
                <th>{t('invoices.client')}</th>
                <th>{t('invoices.date')}</th>
                <th>{t('invoices.dueDate')}</th>
                <th className="text-right">{t('common.amount')}</th>
                <th>{t('common.status')}</th>
                <th className="text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching && !data?.data?.length &&
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j}><div className="skeleton h-4 w-20" /></td>
                    ))}
                  </tr>
                ))}
              {data?.data?.map((inv) => (
                <Fragment key={inv.id}>
                <tr className={`cursor-pointer ${ROW_COLORS[inv.status] || ''}`} onClick={() => navigate(getInvoiceTarget(inv.id, inv.status))}>
                  <td>
                    <Link to={getInvoiceTarget(inv.id, inv.status)} className="text-primary-600 font-medium hover:underline">
                      {inv.invoiceNumber || t('invoices.draft')}
                    </Link>
                  {inv.emailSentAt && (
                    <Tooltip text={t('invoices.emailSent')}>
                      <EnvelopeIcon className="h-3.5 w-3.5 text-emerald-500 ml-1 inline" />
                    </Tooltip>
                  )}
                  </td>
                  <td className="text-gray-600">{inv.client?.name}</td>
                  <td className="text-gray-500">{new Date(inv.issueDate).toLocaleDateString()}</td>
                  <td className="text-gray-500">{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '—'}</td>
                  <td className="text-right font-medium">
                    <div>€{Number(inv.totalAmount).toLocaleString('de-BE', { minimumFractionDigits: 2 })}</div>
                    {inv.status === 'partially_paid' && (
                      <div className="text-xs mt-0.5 font-normal">
                        <span className="text-emerald-600">{t('invoices.paidAmount')}: €{Number(inv.paidAmount).toLocaleString('de-BE', { minimumFractionDigits: 2 })}</span>
                        <span className="text-gray-400 mx-1">·</span>
                        <span className="text-amber-600">{t('invoices.remaining')}: €{(Number(inv.totalAmount) - Number(inv.paidAmount)).toLocaleString('de-BE', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${STATUS_COLORS[inv.status] || 'badge-gray'}`}>
                      {t(`invoices.status.${inv.status}`)}
                    </span>
                  </td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-0.5">
                      {/* ── Invoice actions ── */}
                      <div className="flex items-center gap-0.5 border border-gray-200 rounded-md px-1 py-0.5 bg-white">
                        <Tooltip text={`${t('invoices.actions.download')} — ${inv.invoiceNumber || t('invoices.draft')}`}>
                          <button
                            className="p-1 rounded hover:bg-blue-50 text-blue-600 transition-colors"
                            onClick={() => downloadInvoicePdf(inv.id, `${inv.invoiceNumber || 'draft'}.pdf`).catch(() => toast.error(t('common.error')))}
                          >
                            <DownloadIcon />
                          </button>
                        </Tooltip>
                        <Tooltip text={`${t('invoices.actions.print')} — ${inv.invoiceNumber || t('invoices.draft')}`}>
                          <button
                            className="p-1 rounded hover:bg-blue-50 text-blue-600 transition-colors"
                            onClick={() => openInvoicePdfForPrint(inv.id).catch(() => toast.error(t('common.error')))}
                          >
                            <PrintIcon />
                          </button>
                        </Tooltip>
                        {(inv.status === 'draft' || inv.status === 'sent') && (
                          <Tooltip text={t('invoices.actions.sendToClient')}>
                            <button
                              className="p-1 rounded hover:bg-emerald-50 text-emerald-600 transition-colors"
                              onClick={() => {
                                setSendLanguage((inv as any).client?.language || 'en');
                                setSendModalInvoice(inv);
                              }}
                            >
                              <SendIcon />
                            </button>
                          </Tooltip>
                        )}
                        <Tooltip text={t('activity.viewActivity')}>
                          <button
                            className="p-1 rounded hover:bg-blue-50 text-blue-600 transition-colors"
                            onClick={() => setActivityInvoice({ id: inv.id, number: inv.invoiceNumber || t('invoices.draft') })}
                          >
                            <InformationCircleIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </td>
                </tr>

                {/* ── Credit note rows ── */}
                {inv.creditNotes && inv.creditNotes.length > 0 && inv.creditNotes.map((cn) => (
                  <tr
                    key={cn.id}
                    className="cursor-pointer bg-red-50/40 hover:bg-red-50"
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                  >
                    <td>
                      <Link
                        to={`/invoices/${inv.id}`}
                        className="text-red-700 font-medium hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {cn.creditNoteNumber}
                      </Link>
                      <div className="text-xs text-gray-500">
                        {t('invoices.creditNoteFor', { number: inv.invoiceNumber || t('invoices.draft') })}
                      </div>
                    </td>
                    <td className="text-gray-600">{inv.client?.name}</td>
                    <td className="text-gray-500">{new Date(cn.issueDate).toLocaleDateString()}</td>
                    <td className="text-gray-400">—</td>
                    <td className="text-right font-medium text-red-700">
                      −€{Number(cn.totalAmount).toLocaleString('de-BE', { minimumFractionDigits: 2 })}
                    </td>
                    <td>
                      <span className="badge bg-red-100 text-red-700 border border-red-200">
                        {t('invoices.status.creditNote')}
                      </span>
                    </td>
                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-0.5">
                        <div className="flex items-center gap-0.5 border border-red-200 rounded-md px-1 py-0.5 bg-red-50">
                          <Tooltip text={`${t('invoices.actions.showCreditNote')} — ${cn.creditNoteNumber}`}>
                            <Link
                              to={`/invoices/${inv.id}`}
                              className="p-1 rounded hover:bg-red-100 text-red-600 transition-colors inline-flex"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ViewIcon />
                            </Link>
                          </Tooltip>
                          <Tooltip text={`${t('invoices.actions.downloadCreditNote')} — ${cn.creditNoteNumber}`}>
                            <button
                              className="p-1 rounded hover:bg-red-100 text-red-600 transition-colors"
                              onClick={() => downloadCreditNotePdf(inv.id, cn.id, `${cn.creditNoteNumber}.pdf`).catch(() => toast.error(t('common.error')))}
                            >
                              <DownloadIcon />
                            </button>
                          </Tooltip>
                          <Tooltip text={`${t('invoices.actions.printCreditNote')} — ${cn.creditNoteNumber}`}>
                            <button
                              className="p-1 rounded hover:bg-red-100 text-red-600 transition-colors"
                              onClick={() => openCreditNotePdfForPrint(inv.id, cn.id).catch(() => toast.error(t('common.error')))}
                            >
                              <PrintIcon />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
                </Fragment>
              ))}
              {!isFetching && (!data?.data || data.data.length === 0) && (
                <tr>
                  <td colSpan={7} className="text-center text-gray-400 py-12">{t('common.noResults')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data?.meta && data.meta.pages > 1 && (
          <div className="card-body border-t flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {t('common.showing')} {(data.meta.page - 1) * data.meta.limit + 1}-
              {Math.min(data.meta.page * data.meta.limit, data.meta.total)} of {data.meta.total}
            </span>
            <div className="flex gap-1">
              <button
                className="btn btn-secondary btn-sm"
                disabled={data.meta.page <= 1}
                onClick={() => setFilter('page', String(page - 1))}
              >
                ←
              </button>
              <button
                className="btn btn-secondary btn-sm"
                disabled={data.meta.page >= data.meta.pages}
                onClick={() => setFilter('page', String(page + 1))}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Send email modal */}
      {sendModalInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 min-w-[340px] max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">{t('invoices.sendInvoiceTitle')}</h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoices.emailLanguage')}</label>
            <select className="input w-full mb-4" value={sendLanguage} onChange={(e) => setSendLanguage(e.target.value)}>
              {LANGUAGES.map((l) => (<option key={l.code} value={l.code}>{l.label}</option>))}
            </select>
            <div className="flex justify-end gap-2">
              <button className="btn btn-secondary" onClick={() => setSendModalInvoice(null)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" disabled={sendInvoiceEmail.isPending} onClick={() => {
                sendInvoiceEmail.mutate(
                  { id: sendModalInvoice.id, language: sendLanguage },
                  {
                    onSuccess: () => setSendModalInvoice(null),
                  }
                );
              }}>
                {sendInvoiceEmail.isPending ? t('invoices.sending') : t('invoices.actions.sendToClient')}
              </button>
            </div>
          </div>
        </div>
      )}

      {activityInvoice && (
        <ActivityModal
          mode="entity"
          entityType="Invoice"
          entityId={activityInvoice.id}
          isOpen={true}
          onClose={() => setActivityInvoice(null)}
          title={`${t('activity.title')} — ${activityInvoice.number}`}
        />
      )}
    </div>
  );
}
